import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { isType } from '@sorare/core/src/gql';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import {
  SimpleReward,
  getRankSimpleRewards,
  so5RewardConfigsToSimpleRewards,
} from '@sorare/core/src/lib/rewards';

import { RewardOverview } from 'components/rewards/RewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';
import { isFixtureOpened } from 'lib/so5';

import { RewardRange, RewardRangeVariant } from '../RewardRange';
import { CumulativeRankReward } from './RankReward';
import { RankRewardTitle } from './RankRewardTitle';
import { CumulativeRanking } from './Ranking';
import {
  CumulativeRankRewardsQuery,
  CumulativeRankRewardsQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  &.isUpcoming {
    border-radius: var(--double-unit);
    background-color: var(--c-nd-100);
    padding: var(--double-unit);
  }
`;

const StyledBox = styled(Box)`
  background-color: var(--c-black);
`;

type Props = {
  userGroupSlug: string;
  activeLineupId?: string;
  withTotalPrizePool?: boolean;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string
  ) => string;
};

const CUMULATIVE_RANK_REWARDS_QUERY = gql`
  query CumulativeRankRewardsQuery($userGroupSlug: String!) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        mainRarityType
        membershipsCount
        startSo5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
        myMembership {
          id
          score
          ranking
          ...CumulativeRanking_So5UserGroupMembership
          ...CumulativeRankReward_So5UserGroupMembership
        }
        rewardsConfig {
          ranking {
            ...CumulativeRankReward_so5RewardConfig
            ...getRankSimpleRewards_so5RewardConfig
          }
        }
      }
    }
  }
  ${isFixtureOpened.fragments.so5Fixture}
  ${CumulativeRankReward.fragments.so5RewardConfig}
  ${getRankSimpleRewards.fragments.so5RewardConfig}
  ${CumulativeRanking.fragments.So5UserGroupMembership}
  ${CumulativeRankReward.fragments.So5UserGroupMembership}
` as TypedDocumentNode<
  CumulativeRankRewardsQuery,
  CumulativeRankRewardsQueryVariables
>;

type TotalPrizePoolWrapperProps = {
  simpleRewards: SimpleReward[];
  children?: ReactNode;
};
const TotalPrizePoolWrapper = ({
  simpleRewards,
  children,
}: TotalPrizePoolWrapperProps) => {
  return (
    <>
      <SBHorizontal gap={4}>
        <BodyM as="div">
          <FormattedMessage {...fantasy.prizePool} />
        </BodyM>
        <Horizontal>
          {simpleRewards.map((simpleReward, i) => (
            <RewardOverview
              // eslint-disable-next-line react/no-array-index-key
              key={`simple_${i}`}
              simpleReward={simpleReward}
              variant={Variant.INLINE}
            />
          ))}
        </Horizontal>
      </SBHorizontal>

      <StyledBox>
        <Vertical gap={2}>{children}</Vertical>
      </StyledBox>
    </>
  );
};

export const CumulativeRankRewards = ({
  userGroupSlug,
  activeLineupId,
  withTotalPrizePool = false,
  withCollapsibleRanking = false,
  rankingLink,
}: Props) => {
  const { exchangeRate } = useConfigContext();
  const { data } = useQuery(CUMULATIVE_RANK_REWARDS_QUERY, {
    variables: { userGroupSlug },
  });

  const userGroup = data?.so5?.so5UserGroup;

  if (!userGroup) return null;

  const isUpcoming =
    userGroup.startSo5Fixture && isFixtureOpened(userGroup.startSo5Fixture);
  const rankRewards = getRankSimpleRewards({
    so5RewardConfigs: userGroup?.rewardsConfig.ranking || [],
    rates: exchangeRate.ethRates,
    rarity: userGroup.mainRarityType,
  });

  const myRangeIndex = userGroup.myMembership
    ? rankRewards.findIndex(
        rankReward =>
          (userGroup.myMembership?.ranking || 0) >=
            (rankReward.startRank || 0) &&
          (userGroup.myMembership?.ranking || 0) <= (rankReward.endRank || 0)
      )
    : -1;

  const isMyRankingNotRewarded = myRangeIndex === -1;
  const lastRange = rankRewards[rankRewards.length - 1];

  const simpleRewards = so5RewardConfigsToSimpleRewards({
    so5RewardConfigs: rankRewards,
    rarity: userGroup.mainRarityType || null,
    rates: exchangeRate.ethRates,
  });

  return (
    <Wrapper gap={2} className={classNames({ isUpcoming })}>
      <ConditionalWrapper
        Wrapper={TotalPrizePoolWrapper}
        props={{ simpleRewards }}
        wrap={withTotalPrizePool}
      >
        {rankRewards.map((rankReward, i) => {
          const isMyNextRange = myRangeIndex ? i === myRangeIndex - 1 : false;
          const isLastRange = i === rankRewards.length - 1;

          return (
            <CumulativeRankReward
              so5RewardConfig={rankReward}
              so5Ranking={
                isType(userGroup.myMembership, 'So5UserGroupMembership')
                  ? userGroup.myMembership
                  : undefined
              }
              simpleRewards={rankReward.simpleRewards}
              isMyRange={myRangeIndex === i}
              isMyNextRange={
                isMyNextRange || (isMyRankingNotRewarded && isLastRange)
              }
              userGroupSlug={userGroupSlug}
              rewardOverviewVariant={Variant.INLINE}
              rewardRangeVariant={
                isUpcoming
                  ? RewardRangeVariant.OVERVIEW_ROW
                  : RewardRangeVariant.BLOCK
              }
              key={rankReward.startRank || rankReward.startPct}
              activeLineupId={activeLineupId}
              withCollapsibleRanking={withCollapsibleRanking}
              rankingLink={rankingLink}
            />
          );
        })}
      </ConditionalWrapper>

      {isMyRankingNotRewarded &&
        isType(userGroup.myMembership, 'So5UserGroupMembership') && (
          <RewardRange
            variant={RewardRangeVariant.BLOCK}
            title={
              <RankRewardTitle
                startRank={lastRange.endRank}
                startPct={lastRange.endPct}
                endRank={userGroup.membershipsCount}
                endPct={null}
              />
            }
            rewards={[]}
          >
            <CumulativeRanking
              so5Ranking={userGroup.myMembership}
              userGroupSlug={userGroupSlug}
              activeLineupId={activeLineupId}
              isMyRanking
            />
          </RewardRange>
        )}
    </Wrapper>
  );
};
